import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ShoppingCart, ArrowLeft, Plus, Minus, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import storeService, { Product } from '@/api/services/storeService';
import { AxiosError } from 'axios';
import { getErrorMessage } from '@/utils/errorUtils';

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [product, setProduct]         = useState<Product | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [quantity, setQuantity]       = useState(1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);
  const [message, setMessage]         = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // Format price with thousand separators and currency symbol/code
  const formatPrice = (amount: number, currency?: string): string => {
    const symbol = currency && currency.length === 1 ? currency : '$';
    return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };


  useEffect(() => {
    if (!id) return;
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await storeService.getProductById(id);
        setProduct(data);
      } catch (err: unknown) {
        console.error('Product fetch error:', err);
        setError(
          getErrorMessage(err) ??
          t('operator.productDetail.errors.notFound', { defaultValue: 'Product not found' })
        );
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  // ── Add to cart ───────────────────────────────────────────────────────────
  const handleAddToCart = async () => {
    if (!product) return;
    setAddingToCart(true);
    try {
      await storeService.addToCart({ productId: product.id, quantity });
      showMessage('success', t('operator.productDetail.addedToCart', { defaultValue: 'Added to cart!' }));
      window.dispatchEvent(new Event('cartUpdated'));
      setTimeout(() => navigate('/operator/cart'), 1500);
    } catch (err: unknown) {
      const axiosErr = err instanceof AxiosError
        ? err as AxiosError<{ message?: string }>
        : null;
      const apiMsg = axiosErr?.response?.data?.message;
      const statusCode = axiosErr?.response?.status;
      let errMsg = apiMsg || t('operator.productDetail.errors.addToCart', {
        defaultValue: 'Failed to add to cart. Please try again.',
      });
      if (statusCode === 401) {
        errMsg = t('operator.productDetail.errors.unauthorized', {
          defaultValue: 'Please sign in to add items to cart.',
        });
        setTimeout(() => navigate('/signin'), 2000);
      }
      showMessage('error', errMsg);
    } finally {
      setAddingToCart(false);
    }
  };

  const isAvailable = product
    ? product.stockAvailable !== undefined
      ? product.stockAvailable
      : product.stockStatus === 'in_stock'
    : false;

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#2E5A88] mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('operator.productDetail.loading', { defaultValue: 'Loading product...' })}
          </p>
        </div>
      </div>
    );
  }

  // ── Error / Not found ─────────────────────────────────────────────────────
  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-6 font-medium">
            {error ?? t('operator.productDetail.errors.notFound', { defaultValue: 'Product not found' })}
          </p>
          <button
            onClick={() => navigate('/operator/store')}
            className="mt-4 px-6 py-2.5 bg-[#2E5A88] hover:bg-[#244a73] text-white font-semibold rounded-lg transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('operator.productDetail.backToStore', { defaultValue: 'Back to Store' })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('operator.productDetail.metaTitle', { defaultValue: '{{name}} | KonectX Store', name: product.name })}</title>
        <meta name="description" content={product.shortDescription || product.name} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 py-8">

          {/* ── Toast ── */}
          {message && (
            <div className="fixed top-4 right-4 z-50 max-w-sm w-full">
              <div className={`p-4 rounded-xl shadow-lg flex items-start gap-3 border ${
                message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700'
                  : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700'
              }`}>
                {message.type === 'success'
                  ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  : <XCircle    className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                <p className="text-sm font-medium flex-1">{message.text}</p>
                <button onClick={() => setMessage(null)} className="opacity-60 hover:opacity-100">
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Back button ── */}
          <button
            onClick={() => navigate('/operator/store')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-[#2E5A88] transition-colors mb-6 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('operator.productDetail.backToStore', { defaultValue: 'Back to Store' })}
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* ── Image ── */}
            <div className="bg-white dark:bg-[#333436] rounded-lg p-8">
              <div className="aspect-square bg-gray-100 dark:bg-[#1a1b1e] rounded-lg flex items-center justify-center">
                <img
                  src={product.images?.[activeImageIndex] ?? product.images?.[0] ?? product.thumbnailUrl}
                  alt={product.name}
                  className="w-full h-full object-contain p-8"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      'https://placehold.co/400x400/1a1a2e/60a5fa?text=KonectX';
                  }}
                />
              </div>

              {/* Thumbnail strip if multiple images */}
              {product.images && product.images.length > 1 && (
                <div className="flex gap-2 mt-4">
                  {product.images.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setActiveImageIndex(i)}
                      aria-label={t('operator.productDetail.viewImage', {
                        defaultValue: 'View image {{n}}',
                        n: i + 1,
                      })}
                      className={`w-16 h-16 rounded border-2 transition-all overflow-hidden ${
                        activeImageIndex === i
                          ? 'border-[#2E5A88] ring-2 ring-[#2E5A88]/30'
                          : 'border-gray-200 dark:border-gray-700 hover:border-[#2E5A88]/50'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${i + 1}`}
                        className="w-full h-full object-contain"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Info ── */}
            <div className="space-y-6">
              <div>
                <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-2">{product.name}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 uppercase">{product.category}</p>
              </div>

              <div className="flex items-center gap-6">
                <span className="text-4xl font-bold text-gray-800 dark:text-white">
                  {formatPrice(product.price, product.currency)}
                </span>
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  isAvailable
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {isAvailable
                    ? t('operator.productDetail.inStock', { defaultValue: 'In Stock' })
                    : t('operator.productDetail.outOfStock', { defaultValue: 'Out of Stock' })}
                </span>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-2">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className={`text-xl ${i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-300'}`}>★</span>
                ))}
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ({t('operator.productDetail.reviews', {
                    defaultValue: '{{count}} reviews',
                    count: product.reviewCount || 0,
                  })})
                </span>
              </div>

              <div className="border-t border-b border-gray-200 dark:border-gray-700 py-6">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">
                  {t('operator.productDetail.description', { defaultValue: 'Product Description' })}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {product.fullDescription ?? product.shortDescription}
                </p>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('operator.productDetail.quantity', { defaultValue: 'Quantity' })}
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                      aria-label={t('operator.productDetail.decreaseQuantity', { defaultValue: 'Decrease quantity' })}
                      className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Minus className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    </button>
                    <span className="px-6 text-lg font-semibold text-gray-800 dark:text-white">{quantity}</span>
                    <button
                      onClick={() => {
                        const max = ((product as unknown as Record<string, unknown>).stockQuantity as number) || 99;
                        setQuantity((q) => Math.min(max, q + 1));
                      }}
                      disabled={quantity >= (((product as unknown as Record<string, unknown>).stockQuantity as number) || 99)}
                      aria-label={t('operator.productDetail.increaseQuantity', { defaultValue: 'Increase quantity' })}
                      className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    </button>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {t('operator.productDetail.total', { defaultValue: 'Total' })}: {formatPrice(product.price * quantity, product.currency)}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={handleAddToCart}
                  disabled={!isAvailable || addingToCart}
                  className="w-full py-4 bg-[#2E5A88] hover:bg-[#244a73] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg font-semibold shadow-sm"
                >
                  {addingToCart ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('operator.productDetail.adding', { defaultValue: 'Adding to Cart...' })}
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      {t('operator.productDetail.addToCart', { defaultValue: 'Add to Cart' })}
                    </>
                  )}
                </button>
                <button
                  onClick={() => navigate('/operator/cart')}
                  className="w-full py-4 bg-white dark:bg-[#333436] border-2 border-[#2E5A88] text-[#2E5A88] dark:text-[#5a8ec9] rounded-lg hover:bg-[#2E5A88]/5 dark:hover:bg-[#1a1b1e] transition-colors text-lg font-semibold"
                >
                  {t('operator.productDetail.viewCart', { defaultValue: 'View Cart' })}
                </button>
              </div>

              {/* Details */}
              <div className="bg-gray-100 dark:bg-[#333436] rounded-lg p-6">
                <h3 className="font-semibold text-gray-800 dark:text-white mb-3">
                  {t('operator.productDetail.detailsHeading', { defaultValue: 'Product Details' })}
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('operator.productDetail.category', { defaultValue: 'Category' })}:
                    </span>
                    <span className="font-medium text-gray-800 dark:text-white capitalize">
                      {product.details?.category ?? product.category}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('operator.productDetail.stockStatus', { defaultValue: 'Stock Status' })}:
                    </span>
                    <span className="font-medium text-gray-800 dark:text-white">
                      {product.details?.stockStatus ??
                        (isAvailable
                          ? t('operator.productDetail.available', { defaultValue: 'Available' })
                          : t('operator.productDetail.outOfStock', { defaultValue: 'Out of Stock' }))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('operator.productDetail.currency', { defaultValue: 'Currency' })}:
                    </span>
                    <span className="font-medium text-gray-800 dark:text-white uppercase">
                      {product.currency ?? 'USD'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDetail;